import { useState, useEffect, useRef } from 'react';
import Navbar from '../home/components/Navbar';

function useCountUp(target: number, duration: number = 2000, startOnView: boolean = true) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!startOnView) {
      setStarted(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStarted(true); },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [startOnView]);

  useEffect(() => {
    if (!started) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [started, target, duration]);

  return { count, ref };
}

export default function AboutPage() {
  const [activeValue, setActiveValue] = useState<number | null>(null);
  const [mobileVisibleValues, setMobileVisibleValues] = useState<Set<number>>(new Set());
  const valueRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [projectFormData, setProjectFormData] = useState({
    name: '',
    email: '',
    organization: '',
    phone: '',
    projectDescription: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const stat1 = useCountUp(12, 2000);
  const stat2 = useCountUp(3, 2000);
  const stat3 = useCountUp(6, 2000);
  const stat4 = useCountUp(100, 2000);

  // Close modal with Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showProjectModal) {
        setShowProjectModal(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showProjectModal]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showProjectModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showProjectModal]);

  // Auto-deploy values on scroll for mobile
  useEffect(() => {
    const isMobile = window.matchMedia('(max-width: 639px)').matches;
    if (!isMobile) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const idx = Number(entry.target.getAttribute('data-value-index'));
          if (entry.isIntersecting && !isNaN(idx)) {
            setMobileVisibleValues(prev => {
              const next = new Set(prev);
              next.add(idx);
              return next;
            });
          }
        });
      },
      { threshold: 0.5 }
    );

    valueRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleProjectSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const response = await fetch('https://us-central1-prebedai-web.cloudfunctions.net/sendEmail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: projectFormData.name,
          email: projectFormData.email,
          hospital: projectFormData.organization,
          phone: projectFormData.phone,
          message: projectFormData.projectDescription,
          type: 'project'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setSubmitStatus('error');
        setErrorMessage(data.message || 'Error al enviar el formulario. Por favor, inténtalo de nuevo.');
        return;
      }

      setSubmitStatus('success');
      setProjectFormData({ name: '', email: '', organization: '', phone: '', projectDescription: '' });
      
      setTimeout(() => {
        setSubmitStatus('idle');
        setShowProjectModal(false);
      }, 3000);
      
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage('Error de conexión. Por favor, verifica tu conexión a internet e inténtalo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const values = [
    {
      icon: 'ri-lightbulb-flash-line',
      title: 'Innovación con propósito',
      description: 'No innovamos por innovar. Cada solución nace de un problema real.',
      color: '#06B6D4',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Rigor y evidencia',
      description: 'Trabajamos bajo estándares de calidad, con validación clínica y cumplimiento normativo como pilares de cada proyecto.',
      color: '#8B5CF6',
    },
    {
      icon: 'ri-hand-heart-line',
      title: 'Impacto en el paciente',
      description: 'El objetivo final siempre es el mismo: mejorar la experiencia del paciente y la eficiencia de los profesionales que lo atienden.',
      color: '#06B6D4',
    },
    {
      icon: 'ri-team-line',
      title: 'Colaboración abierta',
      description: 'Trabajamos junto a partners especializados, integrando capacidades para ofrecer la mejor respuesta a cada proyecto.',
      color: '#8B5CF6',
    },
  ];

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero Section - Clean, consistent with platform */}
      <section className="relative py-20 sm:py-28 bg-white overflow-hidden">
        <div className="absolute inset-0 dots-pattern"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Left - Content */}
            <div className="space-y-6 pt-12 sm:pt-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100">
                <i className="ri-building-4-line text-primary-500"></i>
                <span className="text-gray-700 text-sm font-medium">Sobre Nosotros</span>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
                <span className="text-gray-900">Innovación centrada en las personas: </span>
                <span className="gradient-text">paciente y profesional</span>
              </h1>

              <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-xl">
                Iniciativa de innovación tecnológica orientada al sector sanitario, centrada en el diseño y desarrollo de soluciones que integran hardware, software e inteligencia artificial para abordar retos reales en hospitales, clínicas, centros de atención primaria y servicios de atención domiciliaria.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="#contact"
                  className="btn-primary flex items-center justify-center gap-2"
                >
                  <span>Hablemos de tu proyecto</span>
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* Right - Image with integrated quote */}
            <div className="relative mt-8 sm:mt-16 lg:mt-20">
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-primary-500/10 to-[#8B5CF6]/10 rounded-full blur-2xl"></div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-gradient-to-tr from-[#8B5CF6]/10 to-primary-500/10 rounded-full blur-2xl"></div>

              {/* Main image container */}
              <div className="relative">
                <div 
                  className="relative rounded-2xl overflow-hidden shadow-xl aspect-[4/3] select-none"
                  onContextMenu={(e) => e.preventDefault()}
                >
                  <img
                    src="/team.jpg"
                    alt="Presentación innovación sanitaria"
                    className="w-full h-full object-cover pointer-events-none select-none"
                    draggable="false"
                    onDragStart={(e) => e.preventDefault()}
                    style={{ WebkitTouchCallout: 'none' }}
                  />
                  {/* Subtle gradient overlay at bottom for depth */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
                </div>

                {/* Accent border line */}
                <div className="absolute -inset-[1px] rounded-2xl border border-white/50 pointer-events-none"></div>

                {/* Quote card - below on mobile, overlapping on desktop */}
                <div className="sm:absolute sm:-bottom-8 sm:left-6 sm:right-6 mt-4 sm:mt-0 bg-white/95 backdrop-blur-md rounded-xl p-4 sm:p-5 shadow-lg border border-gray-100">
                  <div className="flex gap-3 sm:gap-4">
                    <i className="ri-double-quotes-l text-2xl sm:text-3xl text-primary-500/60 flex-shrink-0 leading-none"></i>
                    <div>
                      <p className="text-gray-700 text-xs sm:text-sm leading-relaxed italic">
                        "Creemos que la tecnología debe ser un puente entre la eficiencia operativa y la atención humana. 
                        Cada línea de código, cada sensor, cada algoritmo que desarrollamos tiene un propósito claro: 
                        mejorar vidas."
                      </p>
                      <div className="mt-2 flex items-center gap-2">
                        <div className="w-6 h-[2px] bg-gradient-to-r from-primary-500 to-[#8B5CF6] rounded-full"></div>
                        <span className="text-xs text-gray-500 font-semibold tracking-wide uppercase">Javier Urrea</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom spacing for the overlapping card - only on desktop */}
              <div className="hidden sm:block h-12"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-gray-50 border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <div ref={stat1.ref} className="text-center">
              <p className="text-3xl sm:text-4xl font-black text-gray-900">+{stat1.count}</p>
              <p className="text-sm text-gray-500 mt-1">Años en el sector salud</p>
            </div>
            <div ref={stat2.ref} className="text-center">
              <p className="text-3xl sm:text-4xl font-black gradient-text">{stat2.count}</p>
              <p className="text-sm text-gray-500 mt-1">Verticales de innovación</p>
            </div>
            <div ref={stat3.ref} className="text-center">
              <p className="text-3xl sm:text-4xl font-black text-gray-900">{stat3.count}+</p>
              <p className="text-sm text-gray-500 mt-1">Proyectos activos</p>
            </div>
            <div ref={stat4.ref} className="text-center">
              <p className="text-3xl sm:text-4xl font-black gradient-text">{stat4.count}%</p>
              <p className="text-sm text-gray-500 mt-1">Foco en salud</p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do - Expertise Areas */}
      <section className="py-16 sm:py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 dots-pattern"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16 space-y-3">
            <div className="inline-block glass px-4 py-2 rounded-full text-base font-bold border border-primary-400/30">
              <span className="text-primary-500">Qué Hacemos</span>
            </div>
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black text-gray-900" style={{ fontFamily: 'Inter, sans-serif' }}>
              Tres verticales, un <span className="gradient-text">mismo propósito</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
              Desarrollamos e integramos dispositivos, software e inteligencia artificial para crear soluciones completas, de principio a fin.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
            <div className="group bg-white rounded-2xl p-6 sm:p-8 border border-gray-200 hover:border-primary-300 hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary-500 transition-colors duration-300">
                <i className="ri-cpu-line text-2xl text-primary-600 group-hover:text-white transition-colors duration-300"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Hardware</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Diseño y desarrollo de dispositivos conectados para monitorización, recogida de datos, seguimiento de pacientes, trazabilidad, verificación, entre otros.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <i className="ri-check-line text-primary-500"></i>
                  <span>Wearables</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <i className="ri-check-line text-primary-500"></i>
                  <span>Tecnología IoT</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <i className="ri-check-line text-primary-500"></i>
                  <span>Diseño bajo normativa CE/MDR</span>
                </div>
              </div>
            </div>

            <div className="group bg-white rounded-2xl p-6 sm:p-8 border border-gray-200 hover:border-[#8B5CF6]/40 hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 bg-[#8B5CF6]/10 rounded-xl flex items-center justify-center mb-5 group-hover:bg-[#8B5CF6] transition-colors duration-300">
                <i className="ri-code-s-slash-line text-2xl text-[#8B5CF6] group-hover:text-white transition-colors duration-300"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Software</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Plataformas web y apps móviles para gestión hospitalaria, seguimiento de pacientes, coordinación de equipos, apoyo a la toma de decisiones, entre muchos otros casos.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <i className="ri-check-line text-[#8B5CF6]"></i>
                  <span>Plataformas SaaS</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <i className="ri-check-line text-[#8B5CF6]"></i>
                  <span>Apps móviles</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <i className="ri-check-line text-[#8B5CF6]"></i>
                  <span>Integración con sistemas</span>
                </div>
              </div>
            </div>

            <div className="group bg-white rounded-2xl p-6 sm:p-8 border border-gray-200 hover:border-primary-300 hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary-500 transition-colors duration-300">
                <i className="ri-brain-line text-2xl text-primary-600 group-hover:text-white transition-colors duration-300"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Inteligencia Artificial</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Modelos predictivos, visión por computador y procesamiento de datos clínicos para automatizar análisis y apoyar decisiones de los profesionales.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <i className="ri-check-line text-primary-500"></i>
                  <span>Modelos predictivos</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <i className="ri-check-line text-primary-500"></i>
                  <span>Análisis de imagen y datos</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <i className="ri-check-line text-primary-500"></i>
                  <span>Procesamiento lenguaje natural</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section - Interactive */}
      <section className="py-16 sm:py-24 bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 dots-pattern"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16 space-y-3">
            <div className="inline-block glass px-4 py-2 rounded-full text-base font-bold border border-[#8B5CF6]/30">
              <span className="text-[#8B5CF6]">Nuestros Valores</span>
            </div>
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black text-gray-900" style={{ fontFamily: 'Inter, sans-serif' }}>
              Lo que nos <span className="gradient-text">define</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, i) => {
              const isExpanded = activeValue === i || mobileVisibleValues.has(i);
              return (
              <div
                key={i}
                ref={(el) => { valueRefs.current[i] = el; }}
                data-value-index={i}
                className={`bg-white rounded-2xl p-6 border-2 cursor-pointer transition-all duration-300 ${
                  isExpanded
                    ? 'border-primary-400 shadow-lg scale-[1.02]'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                }`}
                onClick={() => setActiveValue(activeValue === i ? null : i)}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-all duration-300"
                  style={{ backgroundColor: `${value.color}15` }}
                >
                  <i className={`${value.icon} text-2xl`} style={{ color: value.color }}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{value.title}</h3>
                <p className={`text-sm text-gray-600 leading-relaxed transition-all duration-300 ${
                  isExpanded ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0 overflow-hidden sm:max-h-40 sm:opacity-100'
                }`}>
                  {value.description}
                </p>
                <i className={`ri-arrow-down-s-line text-gray-400 mt-2 block sm:hidden transition-transform duration-300 ${
                  isExpanded ? 'rotate-180' : ''
                }`}></i>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Collaborative Model */}
      <section className="py-16 sm:py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 dots-pattern"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block glass px-4 py-2 rounded-full text-base font-bold border border-primary-400/30">
                <span className="text-primary-500">Modelo de Trabajo</span>
              </div>
              <h2 className="text-2xl sm:text-4xl font-black text-gray-900" style={{ fontFamily: 'Inter, sans-serif' }}>
                Red de partners<br />
                <span className="gradient-text">especializados</span>
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Trabajamos con un modelo ágil basado en la colaboración con partners y profesionales 
                especializados según las necesidades de cada proyecto. Esto nos permite escalar, adaptarnos 
                y ofrecer la mejor solución sin comprometer la calidad ni la especialización.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Cada equipo se configura de manera única: ingenieros biomédicos, desarrolladores full-stack, 
                especialistas en IA, diseñadores UX del sector salud y consultores clínicos. 
                Un ecosistema flexible con un núcleo sólido.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-2xl p-5 border border-gray-200 hover:shadow-md transition-all duration-300">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mb-3">
                  <i className="ri-flask-line text-lg text-primary-600"></i>
                </div>
                <h4 className="font-bold text-gray-900 text-sm mb-1">Ingeniería biomédica</h4>
                <p className="text-xs text-gray-500">Diseño de hardware y dispositivos médicos</p>
              </div>
              <div className="bg-gray-50 rounded-2xl p-5 border border-gray-200 hover:shadow-md transition-all duration-300">
                <div className="w-10 h-10 bg-[#8B5CF6]/10 rounded-lg flex items-center justify-center mb-3">
                  <i className="ri-terminal-box-line text-lg text-[#8B5CF6]"></i>
                </div>
                <h4 className="font-bold text-gray-900 text-sm mb-1">Desarrollo software</h4>
                <p className="text-xs text-gray-500">Full-stack, mobile, cloud & DevOps</p>
              </div>
              <div className="bg-gray-50 rounded-2xl p-5 border border-gray-200 hover:shadow-md transition-all duration-300">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mb-3">
                  <i className="ri-robot-2-line text-lg text-primary-600"></i>
                </div>
                <h4 className="font-bold text-gray-900 text-sm mb-1">Data Science & IA</h4>
                <p className="text-xs text-gray-500">Machine learning y modelos predictivos</p>
              </div>
              <div className="bg-gray-50 rounded-2xl p-5 border border-gray-200 hover:shadow-md transition-all duration-300">
                <div className="w-10 h-10 bg-[#8B5CF6]/10 rounded-lg flex items-center justify-center mb-3">
                  <i className="ri-stethoscope-line text-lg text-[#8B5CF6]"></i>
                </div>
                <h4 className="font-bold text-gray-900 text-sm mb-1">Consultoría clínica</h4>
                <p className="text-xs text-gray-500">Validación y adecuación al entorno real</p>
              </div>
              <div className="bg-gray-50 rounded-2xl p-5 border border-gray-200 hover:shadow-md transition-all duration-300 col-span-2">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mb-3">
                  <i className="ri-layout-4-line text-lg text-primary-600"></i>
                </div>
                <h4 className="font-bold text-gray-900 text-sm mb-1">Diseño UX</h4>
                <p className="text-xs text-gray-500">Experiencias de usuario optimizadas para profesionales sanitarios y pacientes</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid - Compact Portfolio */}
      <section className="py-16 sm:py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 dots-pattern"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16 space-y-3">
            <div className="inline-block glass px-4 py-2 rounded-full text-base font-bold border border-[#8B5CF6]/30">
              <span className="text-[#8B5CF6]">Portafolio</span>
            </div>
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black text-gray-900" style={{ fontFamily: 'Inter, sans-serif' }}>
              Soluciones que <span className="gradient-text">transforman</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Weebo */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:border-primary-300 hover:shadow-lg transition-all duration-300 group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center group-hover:bg-primary-500 transition-colors duration-300">
                  <i className="ri-heart-pulse-line text-xl text-primary-600 group-hover:text-white transition-colors duration-300"></i>
                </div>
                <div>
                  <span className="text-[10px] font-semibold text-primary-500 uppercase tracking-wider">Dispositivo</span>
                  <h3 className="text-base font-bold text-gray-900 leading-tight">Weebo</h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                Verificación segura paciente–medicación y trazabilidad en el punto de administración.
                Monitoriza infusiones IV en tiempo real y genera alertas ante incidencias.
              </p>
            </div>

            {/* MycareSoft */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:border-[#8B5CF6]/40 hover:shadow-lg transition-all duration-300 group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#8B5CF6]/10 rounded-lg flex items-center justify-center group-hover:bg-[#8B5CF6] transition-colors duration-300">
                  <i className="ri-dashboard-line text-xl text-[#8B5CF6] group-hover:text-white transition-colors duration-300"></i>
                </div>
                <div>
                  <span className="text-[10px] font-semibold text-[#8B5CF6] uppercase tracking-wider">Software</span>
                  <h3 className="text-base font-bold text-gray-900 leading-tight">MycareSoft</h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                Plataforma clínica que visualiza en tiempo real el suministro IV y centraliza toda la trazabilidad de medicación.
                Dashboards y alertas inteligentes para prevenir errores, detectar incidencias y coordinar la actuación del equipo.
              </p>
            </div>

            {/* MycareTrack */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:border-primary-300 hover:shadow-lg transition-all duration-300 group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center group-hover:bg-primary-500 transition-colors duration-300">
                  <i className="ri-home-heart-line text-xl text-primary-600 group-hover:text-white transition-colors duration-300"></i>
                </div>
                <div>
                  <span className="text-[10px] font-semibold text-primary-500 uppercase tracking-wider">Software + IA</span>
                  <h3 className="text-base font-bold text-gray-900 leading-tight">MycareTrack</h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                Plataforma de atención domiciliaria que conecta paciente, cuidadores, familiares y profesionales en un mismo entorno.
                Seguimiento clínico, registro de visitas y cuidados, medición de heridas y control de suministros.
              </p>
            </div>

            {/* Urotrack */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:border-[#8B5CF6]/40 hover:shadow-lg transition-all duration-300 group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#8B5CF6]/10 rounded-lg flex items-center justify-center group-hover:bg-[#8B5CF6] transition-colors duration-300">
                  <i className="ri-smartphone-line text-xl text-[#8B5CF6] group-hover:text-white transition-colors duration-300"></i>
                </div>
                <div>
                  <span className="text-[10px] font-semibold text-[#8B5CF6] uppercase tracking-wider">App Móvil + IA</span>
                  <h3 className="text-base font-bold text-gray-900 leading-tight">Urotrack</h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                App móvil para prevención y seguimiento de infecciones urinarias, con control de síntomas y hábitos.
                Conecta paciente y profesional, mejora la adherencia y facilita intervención temprana.
              </p>
            </div>

            {/* MycarehAIr */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:border-primary-300 hover:shadow-lg transition-all duration-300 group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center group-hover:bg-primary-500 transition-colors duration-300">
                  <i className="ri-brain-line text-xl text-primary-600 group-hover:text-white transition-colors duration-300"></i>
                </div>
                <div>
                  <span className="text-[10px] font-semibold text-primary-500 uppercase tracking-wider">Software + IA</span>
                  <h3 className="text-base font-bold text-gray-900 leading-tight">MycarehAIr</h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                Plataforma con IA que analiza imágenes capilares y cuantifica la evolución del tratamiento.
                Comparativas, Norwood–Hamilton e informes con métricas objetivas de densidad y cobertura.
              </p>
            </div>

            {/* PreBedAI - sin énfasis especial */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:border-[#8B5CF6]/40 hover:shadow-lg transition-all duration-300 group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#8B5CF6]/10 rounded-lg flex items-center justify-center group-hover:bg-[#8B5CF6] transition-colors duration-300">
                  <i className="ri-hospital-line text-xl text-[#8B5CF6] group-hover:text-white transition-colors duration-300"></i>
                </div>
                <div>
                  <span className="text-[10px] font-semibold text-[#8B5CF6] uppercase tracking-wider">Software + IA</span>
                  <h3 className="text-base font-bold text-gray-900 leading-tight">PreBedAI</h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                SaaS de gestión de camas y predicción de altas, con visión operativa en tiempo real del hospital.
                Optimiza asignación, reduce esperas y coordina equipos mediante automatización e IA.
              </p>
            </div>
          </div>

          {/* On-demand development */}
          <div className="mt-8 bg-gray-50 rounded-2xl p-6 sm:p-8 border border-gray-200">
            <div className="flex items-center gap-6">
              <div className="w-14 h-14 bg-primary-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-tools-line text-2xl text-white"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-1">Desarrollo a medida</h3>
                <p className="text-sm text-gray-600">
                  Diseñamos y desarrollamos soluciones personalizadas adaptadas a las necesidades específicas de cada organización sanitaria.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-16 sm:py-24 bg-gray-50 relative overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-white rounded-3xl p-8 sm:p-12 border border-gray-200 shadow-lg text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-50 mb-6">
              <i className="ri-chat-smile-2-line text-primary-500"></i>
              <span className="text-primary-600 text-sm font-medium">Hablemos</span>
            </div>
            <h2 className="text-2xl sm:text-4xl font-black text-gray-900 mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              ¿Tienes un proyecto en <span className="gradient-text">mente</span>?
            </h2>
            <p className="text-gray-600 mb-8 max-w-xl mx-auto">
              Cuéntanos tu idea o necesidad. La analizaremos juntos y diseñaremos la mejor solución tecnológica para tu organización.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setShowProjectModal(true)}
                className="btn-primary flex items-center justify-center gap-2"
              >
                <span>Contacta con nosotros</span>
                <i className="ri-arrow-right-line"></i>
              </button>
              <a
                href="https://www.linkedin.com/company/prebedai/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary flex items-center justify-center gap-2"
              >
                <i className="ri-linkedin-fill"></i>
                <span>Conectar en LinkedIn</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Modal de Propuesta de Proyecto */}
      {showProjectModal && (
        <div 
          className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowProjectModal(false)}
        >
          <div 
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-100 p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 bg-primary-50 rounded-xl flex items-center justify-center">
                    <i className="ri-mail-send-line text-xl text-primary-500"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Cuéntanos tu proyecto</h3>
                    <p className="text-gray-500 text-sm">La analizaremos juntos y diseñaremos la mejor solución</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowProjectModal(false)}
                  className="w-9 h-9 rounded-lg border border-gray-200 hover:bg-gray-50 flex items-center justify-center transition-colors"
                >
                  <i className="ri-close-line text-gray-400 text-lg"></i>
                </button>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleProjectSubmit} className="p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Nombre completo <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={projectFormData.name}
                    onChange={(e) => setProjectFormData({ ...projectFormData, name: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 focus:bg-white transition-all text-base"
                    placeholder="Tu nombre"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Email <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={projectFormData.email}
                    onChange={(e) => setProjectFormData({ ...projectFormData, email: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 focus:bg-white transition-all text-base"
                    placeholder="tu@email.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Organización <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={projectFormData.organization}
                    onChange={(e) => setProjectFormData({ ...projectFormData, organization: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 focus:bg-white transition-all text-base"
                    placeholder="Hospital / Clínica / Empresa"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Teléfono <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={projectFormData.phone}
                    onChange={(e) => setProjectFormData({ ...projectFormData, phone: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 focus:bg-white transition-all text-base"
                    placeholder="+34 XXX XXX XXX"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Describe tu proyecto <span className="text-primary-500">*</span>
                </label>
                <textarea
                  value={projectFormData.projectDescription}
                  onChange={(e) => setProjectFormData({ ...projectFormData, projectDescription: e.target.value })}
                  required
                  rows={5}
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 focus:bg-white transition-all resize-none text-base"
                  placeholder="Cuéntanos qué necesitas: problema a resolver, objetivos, características deseadas, usuarios finales, etc."
                ></textarea>
              </div>

              {submitStatus === 'success' && (
                <div className="p-4 bg-primary-50 border border-primary-200 rounded-xl">
                  <div className="flex items-center gap-2.5 text-primary-700">
                    <i className="ri-checkbox-circle-line text-lg"></i>
                    <span className="font-medium text-sm">¡Propuesta enviada con éxito! Te contactaremos pronto.</span>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-2 text-red-700">
                      <i className="ri-error-warning-line text-lg"></i>
                      <span className="font-medium text-sm">Error al enviar</span>
                    </div>
                    {errorMessage && (
                      <p className="text-sm text-red-600 pl-6">{errorMessage}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Footer buttons */}
              <div className="flex gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowProjectModal(false)}
                  className="flex-1 btn-secondary text-center"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 btn-primary text-center flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      <span>Enviando...</span>
                    </>
                  ) : (
                    <>
                      <span>Enviar Propuesta</span>
                      <i className="ri-send-plane-line"></i>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
